import clsx from 'clsx';
import React, { useId } from 'react';

import { PrimaryButton, ProButton } from '@components/Button/variants';
import IconCheckMark from '@components/Images/Rebranding/IconCheckMark';
import { ANNUAL_BENEFITS, PRO_BENEFITS } from '@components/pages/DualSubscribe/constants';
import type { Benefit } from '@components/pages/SubscribeTest/interfaces';
import useEventTracking from '@hooks/useEventTracking';
import { subscribe_one_path } from '@routes';
import { ClickTarget, TrackingLocation, click } from '@tracking';
import type { OfferOption } from '@utils/backend-api/subscribe';
import { formatNumber } from '@utils/formatting';
import getSubscriptionPriceBreakdown from '@utils/getSubscriptionPriceBreakdown';

import subscribeParams from '../utils/subscribeParams';

const CORE_BENEFITS = ANNUAL_BENEFITS.filter(({ label }) => label !== 'Audio: listen to articles');
const PRO_MATRIX_BENEFITS = [...CORE_BENEFITS, ...PRO_BENEFITS.filter(({ blank }) => !blank)];

interface SubscribeMatrixTwoColumnProps {
  annualOffer?: OfferOption | null;
  proOffer?: OfferOption | null;
  redirectTo?: string | null;
}

interface OfferColumnProps {
  benefits: Benefit[];
  offer: OfferOption;
  onOfferClick: (offer: OfferOption) => void;
  redirectTo?: string | null;
}

const BenefitList = ({ benefits, isPro }: { benefits: Benefit[]; isPro: boolean }) => (
  <ul className="m-0 flex list-none flex-col gap-16 p-0">
    {benefits.map((benefit, index) => (
      <li key={`${benefit.label ?? 'benefit'}-${index}`} className="flex items-center gap-8">
        <IconCheckMark
          aria-hidden
          className={clsx('h-16 w-24 shrink-0', {
            'text-primary-magenta': !isPro,
            'text-pro-gold': isPro,
          })}
          focusable="false"
        />
        <div
          className={clsx('flex min-w-0', {
            'flex-col gap-0 lg:flex-row lg:gap-8': benefit.stackedBenefits,
            'gap-8': !benefit.stackedBenefits,
          })}
        >
          {benefit.Icon && (
            <>
              <benefit.Icon aria-hidden className="h-24 w-auto shrink-0" focusable="false" />
              <span className="sr-only">Deep Research</span>
            </>
          )}
          {benefit.label && (
            <span className="font-suisse-intl text-button-md font-medium text-grayscale-charcoal">
              {benefit.label}
            </span>
          )}
          {benefit.suffix && (
            <span
              className={clsx(
                'font-suisse-intl text-button-md text-grayscale-charcoal',
                benefit.suffixClassName
              )}
            >
              {benefit.suffix}
            </span>
          )}
        </div>
      </li>
    ))}
  </ul>
);

const OfferColumn = ({ benefits, offer, onOfferClick, redirectTo }: OfferColumnProps) => {
  const headingId = useId();
  const {
    billingFrequency,
    billingFrequencyOverride,
    code,
    currentPrice,
    currentPriceDisplayOverride,
    isPro,
    originalPrice,
    originalPriceDisplayOverride,
  } = offer;
  const { currentPriceToDisplay, hasDiscount, originalPriceToDisplay } =
    getSubscriptionPriceBreakdown({
      currentPrice,
      currentPriceDisplayOverride,
      originalPrice,
      originalPriceDisplayOverride,
    });
  const planName = isPro ? 'Pro' : 'Annual';

  return (
    <article aria-labelledby={headingId} className="flex min-w-0 flex-col">
      <div className="flex flex-col items-start gap-24">
        <div className="flex w-full flex-col gap-8">
          <h2
            id={headingId}
            className="m-0 text-balance font-suisse-works text-headline-sm text-primary-black"
          >
            The Information{' '}
            <span className={isPro ? 'text-pro-gold' : 'text-primary-magenta'}>{planName}</span>
          </h2>
          <div className="flex flex-wrap items-end gap-x-16 gap-y-4 font-suisse-screen">
            <div className="flex items-baseline gap-8">
              <strong className="text-headline-alt-sm leading-[1.3] text-primary-black">
                ${formatNumber(currentPriceToDisplay)}
              </strong>
              {hasDiscount && (
                <del className="text-headline-alt-sm leading-[1.3] text-grayscale-gray">
                  ${formatNumber(originalPriceToDisplay)}
                </del>
              )}
              <span className="text-disclosure-lg leading-[1.7] text-primary-black">
                {billingFrequencyOverride || '/ Year'}
              </span>
            </div>
            <span className="text-byline-md leading-[1.7] text-grayscale-gray">
              Billed {billingFrequency.toLowerCase()}
            </span>
          </div>
        </div>
        {isPro ? (
          <ProButton
            aria-label={`Subscribe to The Information ${planName}`}
            as="link"
            className="min-h-40 w-full sm:w-[270px]"
            href={subscribe_one_path(subscribeParams({ offerCode: code, redirectTo }))}
            onClickCapture={() => onOfferClick(offer)}
            size="small"
          >
            Subscribe
          </ProButton>
        ) : (
          <PrimaryButton
            aria-label={`Subscribe to The Information ${planName}`}
            as="link"
            className="min-h-40 w-full sm:w-[270px]"
            href={subscribe_one_path(subscribeParams({ offerCode: code, redirectTo }))}
            onClickCapture={() => onOfferClick(offer)}
            size="small"
          >
            Subscribe
          </PrimaryButton>
        )}
      </div>
      <div className="mt-40 border-0 border-t border-solid border-grayscale-beige pt-32">
        <BenefitList benefits={benefits} isPro={isPro} />
      </div>
    </article>
  );
};

const SubscribeMatrixTwoColumn = ({
  annualOffer,
  proOffer,
  redirectTo,
}: SubscribeMatrixTwoColumnProps) => {
  const { trackEvent } = useEventTracking({ location: TrackingLocation.OfferTable });
  const handleOfferClick = (offer: OfferOption) => {
    trackEvent(
      click({
        clickTarget: ClickTarget.Offer,
        clickText: ClickTarget.Subscribe,
        offerCode: offer.code,
      })
    );
  };

  if (!annualOffer && !proOffer) return null;

  return (
    <section
      aria-label="Subscription plans"
      className="mx-auto grid w-full max-w-[1174px] grid-cols-2 gap-64 sm:gap-64 lg:gap-[112px] lg:px-[65px]"
    >
      {annualOffer && (
        <OfferColumn
          benefits={CORE_BENEFITS}
          offer={annualOffer}
          onOfferClick={handleOfferClick}
          redirectTo={redirectTo}
        />
      )}
      {proOffer && (
        <OfferColumn
          benefits={PRO_MATRIX_BENEFITS}
          offer={proOffer}
          onOfferClick={handleOfferClick}
          redirectTo={redirectTo}
        />
      )}
    </section>
  );
};

export default SubscribeMatrixTwoColumn;
